/**
 * Import d'un export **Passky** JSON (« Exporter > Passky » du coffre Passky,
 * gestionnaire libre : ses utilisateurs doivent pouvoir partir avec TOUT leur
 * coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier porte `encrypted` (booleen) et `passwords`,
 * un tableau de `{ website, username, password, message }` : `website` est
 * l'adresse (souvent un hote nu, `passky.org`), `message` la note. Toute autre
 * cle non vide devient un champ personnalise, masque si son libelle designe un
 * secret (aucune perte).
 *
 * Une **sauvegarde chiffree** Passky (`encrypted: true`) n'est pas lue : ses
 * valeurs sont scellees par le chiffrement propre a Passky, et Klavox ne
 * reimplemente pas la crypto d'un tiers. L'utilisateur est invite a refaire un
 * export non chiffre, plutot que de recevoir des entrees illisibles.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, type ImportedEntry } from './csv-import.js';
import { serverHost } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

/** Cles reprises par le lecteur, et identifiant interne sans valeur pour l'utilisateur. */
const KNOWN_KEYS = new Set(['website', 'username', 'password', 'message', 'id']);

function parsePasskyRecord(record: Record<string, Json>): ImportedEntry | null {
  const website = asString(record['website']);
  const username = asString(record['username']);
  const password = rawSecret(record['password']);
  // La note garde sa mise en forme (retraits, sauts de ligne), seuls ses bords sont rognes.
  const notes = typeof record['message'] === 'string' ? record['message'].trim() : asString(record['message']);
  const customFields: EntryCustomField[] = [];
  for (const [key, value] of Object.entries(record)) {
    if (KNOWN_KEYS.has(key)) continue;
    const text = asObject(value) !== null || Array.isArray(value) ? JSON.stringify(value) : rawSecret(value);
    if (text.trim().length > 0) customFields.push(csvColumnField(key, text));
  }
  if (website.length === 0 && username.length === 0 && password.length === 0 && notes.length === 0 && customFields.length === 0) {
    return null;
  }
  const host = website.length > 0 ? serverHost(website).replace(/^www\./u, '') : '';
  return {
    type: 'login',
    name: host || username || 'Sans nom',
    username,
    password,
    uris: website.length > 0 ? [website] : [],
    notes,
    totpSecret: '',
    favorite: false,
    ...(customFields.length > 0 ? { customFields } : {}),
  };
}

/**
 * Un export Passky se reconnait au couple `encrypted` (booleen) + tableau
 * `passwords` : aucun autre format lu par Klavox ne porte ces deux cles.
 */
export function isPasskyExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && typeof file['encrypted'] === 'boolean' && Array.isArray(file['passwords']);
}

/**
 * Parse un export Passky JSON non chiffre : chaque mot de passe devient un
 * identifiant. Les enregistrements entierement vides sont ecartes.
 *
 * @throws {Error} sauvegarde chiffree (`encrypted: true`) : elle n'est pas lue
 */
export function parsePasskyExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  if (file === null || !Array.isArray(file['passwords'])) throw new Error('export Passky : structure inattendue');
  if (file['encrypted'] === true) {
    throw new Error('sauvegarde Passky chiffree : exportez de nouveau depuis Passky en choisissant l’export non chiffre (Klavox ne lit pas le chiffrement propre a Passky)');
  }
  return (file['passwords'] as readonly Json[])
    .map(asObject)
    .filter((record): record is Record<string, Json> => record !== null)
    .map(parsePasskyRecord)
    .filter((entry): entry is ImportedEntry => entry !== null);
}
